import { config, dom, library } from '@fortawesome/fontawesome-svg-core';
import '@fortawesome/fontawesome-svg-core/styles.css';
import {
  faArrowRightFromBracket,
  faArrowRotateLeft,
  faBars,
  faBell,
  faBellSlash,
  faBolt,
  faCamera,
  faChartArea,
  faChartBar,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faChevronUp,
  faCircleHalfStroke,
  faCircleInfo,
  faCircleNotch,
  faCircleQuestion,
  faClock,
  faCloudArrowUp,
  faCommentSms,
  faCompress,
  faDatabase,
  faDeleteLeft,
  faDrawPolygon,
  faEarthAmericas,
  faEdit,
  faEllipsis,
  faEnvelope,
  faEraser,
  faExpand,
  faEyeSlash,
  faFileExport,
  faFileImport,
  faFilter,
  faFire,
  faFireFlameSimple,
  faFloppyDisk,
  faGear,
  faGem,
  faGlobe,
  faHammer,
  faHouse,
  faIndustry,
  faLayerGroup,
  faLink,
  faList,
  faLocationCrosshairs,
  faMap,
  faMagnifyingGlass,
  faMinus,
  faMoon,
  faMound,
  faObjectGroup,
  faPlug,
  faPlus,
  faRobot,
  faRuler,
  faRulerHorizontal,
  faSatellite,
  faSatelliteDish,
  faSeedling,
  faSolarPanel,
  faSun,
  faTrash,
  faTree,
  faTrowelBricks,
  faUpload,
  faUser,
  faVolcano,
  faWheatAwn,
  faCloud,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import PrimeVue from 'primevue/config';
import Tooltip from 'primevue/tooltip';
import { createApp } from 'vue';

import App from './App.vue';
import i18n from './i18n';
import { installGlobalErrorHandling, logBootstrapError } from './runtimeErrorHandling';
import { OroraPreset } from './theme';

import 'ol/ol.css';
import './style/tailwind.css';
import './style/main.scss';

function enableWillReadFrequentlyFor2D() {
  if (typeof window === 'undefined' || typeof HTMLCanvasElement === 'undefined') return;

  const canvasProto = HTMLCanvasElement.prototype as typeof HTMLCanvasElement.prototype & {
    __overpassWillReadPatched?: boolean;
  };
  if (canvasProto.__overpassWillReadPatched) return;

  const originalGetContext = HTMLCanvasElement.prototype.getContext;
  Object.defineProperty(HTMLCanvasElement.prototype, 'getContext', {
    configurable: true,
    writable: true,
    value: function patchedGetContext(this: HTMLCanvasElement, contextId: unknown, options?: unknown) {
      if (contextId === '2d') {
        const baseOptions = (options && typeof options === 'object')
          ? options as CanvasRenderingContext2DSettings
          : {};
        return (originalGetContext as any).call(this, '2d', {
          ...baseOptions,
          willReadFrequently: true,
        });
      }
      return (originalGetContext as any).call(this, contextId, options);
    },
  });

  canvasProto.__overpassWillReadPatched = true;
}

// Prevent FA from auto-injecting CSS (we imported the stylesheet above)
config.autoAddCss = false;

// Font Awesome icons used by the kit
library.add(
  faCircleNotch,
  faPlus, faMinus, faExpand, faCompress,
  faSun, faMoon, faCircleHalfStroke,
  faMagnifyingGlass, faLocationCrosshairs, faMap, faDeleteLeft,
  faCamera, faRuler, faRulerHorizontal, faDrawPolygon, faEraser,
  faLayerGroup, faDatabase, faFilter, faObjectGroup,
  faEarthAmericas, faXmark, faChevronLeft, faChevronRight, faChevronDown, faChevronUp, faList, faChartBar, faBars,
  faChartArea,
  faCloud,
  faGlobe, faEnvelope, faLink, faBell,
  faClock, faSatellite, faSatelliteDish, faArrowRotateLeft,
  // Filter panel icons
  faFloppyDisk, faTrash, faCircleInfo, faCircleQuestion,
  faFire, faFireFlameSimple, faTree, faWheatAwn, faSeedling, faMound, faHouse,
  faIndustry, faHammer, faTrowelBricks, faRobot, faGem, faVolcano, faSolarPanel,
  faBolt, faUser, faPlug,
  // Monitor panel icons
  faEllipsis, faUpload, faEdit, faFileExport, faFileImport, faCloudArrowUp,
  faEyeSlash, faBellSlash, faCommentSms,
  faGear, faArrowRightFromBracket,
);

// Watch the DOM and replace <i>/<span> FA class elements with inline SVGs
// (required for PrimeVue Button icon prop to render visible icons)
dom.watch();

enableWillReadFrequentlyFor2D();

const app = createApp(App);

installGlobalErrorHandling(app);

try {
  app.component('FontAwesomeIcon', FontAwesomeIcon);
  app.use(i18n);

  app.use(PrimeVue, {
    theme: {
      preset: OroraPreset,
      options: {
        darkModeSelector: '.color-scheme-dark',
        cssLayer: {
          name: 'primevue',
          order: 'theme, base, primevue',
        },
      },
    },
  });

  app.directive('tooltip', Tooltip);

  app.mount('#app');
} catch (error) {
  logBootstrapError(error);
}
